import { SystemStorageType } from '../../../App/model/system-storage-types';

/**
 * @ngdoc service
 * @name mx.shell.SidePanelService
 * @module mx.shell
 *
 * @description
 * Controls SidePanel, its details views and all internal logic in it
 * such as showing selected objects from {@ref mx.shell.ContextService mx.shell.ContextService} and other.
 */
SidePanelService.$inject = [
	'$q',
	'$mdDialog',
	'$mdSidenav',
	'mx.app.DeviceService',
	'mx.internationalization',
	'mx.shell.EventService',
	'mx.shell.LeftSidebarService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.SolutionBuilderAgent.WidgetManager',
	'mx.shell.SideLayout',
	'mx.shell.View',
	'mx.shell.DetailsView',

	'mx.shell.ContextService',
	'mx.shell.UserProfileService',
	'mx.shell.DataStorage',
	'mx.shell.NotificationService',
	'mx.shell.WizardView',
	'mx.shell.NavigationManager',
	'mx.SolutionBuilderAgent.ContentWidgetService',
];
export function SidePanelService(
	$q,
	$mdDialog,
	$mdSidenav,
	deviceService,
	i18n,
	eventService,
	leftSidebarService,
	personalizationService,
	widgetManager,
	sideLayoutService,
	MxView,
	DetailsView,
	contextService,
	userProfileService,
	dataStorage,
	notificationService,
	WizardView,
	navigationManager,
	contentWidgetService
) {
	const openedViews = [],
		inProgressViews = [];
	let currentView;

	const storage = dataStorage.getStorage(SystemStorageType.LocalStorage);

	const self = {
		isViewUnsafeForClosing: isViewUnsafeForClosing,
		refreshView: refreshView,
		isViewOpened: isViewOpened,
		showCloseTabConfirmationDialog: showCloseTabConfirmationDialog,
		showConfirmationDialog: showConfirmationDialog,

		addView: addView,
		closeView: externalCloseView,
		closeCurrentView: closeCurrentView,
		switchToView: switchToView,
		showView: showView,
		hideView: hideView,
		isViewInProgress: isViewInProgress,

		setCurrentView: setCurrentView,
		getCurrentView: getCurrentView,
		getCurrentMainView: getCurrentMainView,

		getOpenedViews: getOpenedViews,
		// getLoadedViews: getLoadedViews,

		showPopupInTab: showPopupInTab,
		hidePopupInTab: hidePopupInTab,

		handleItemSelectionOnMobile: handleItemSelectionOnMobile,

		getObjectDetailsFromView: getObjectDetailsFromView,
		getSidePanelView: getSidePanelView,
	};

	eventService.subscribeGlobal(mx.shell.EventCodes.RouteLoading, function () {
		setCurrentView(undefined);
	});

	eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ObjectTypeChanged, function (e, params) {
		const object = {
			Id: params.objectId,
			EntityType: params.entityType,
		};
		removeFromHistory(object);
	});

	eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ObjectDataDeleted, function (e, data) {
		if (data && Array.isArray(data.affectedObjectIds)) {
			data.affectedObjectIds.forEach(function (objectId) {
				const object = { Id: objectId };
				removeFromHistory(object);
			});
		}
	});

	eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ViewDirtyStatusChanged, function (e, view) {
		if (!(view instanceof MxView)) {
			return;
		}

		if (isViewOpened(view)) {
			if (view.dirty && !isViewInProgress(view)) {
				moveViewToInProgress(view);
			}
		}
	});

	eventService.subscribeGlobal(mx.shell.EventCodes.Search, (event, searchOptions) => onSearch(searchOptions));

	Object.defineProperty(self, 'currentView', {
		get: function () {
			return self.getCurrentView();
		},
		set: function (view) {
			self.setCurrentView(view);
		},
	});

	Object.defineProperty(self, 'openedViews', {
		get: function () {
			return openedViews;
		},
	});

	Object.defineProperty(self, 'inProgressViews', {
		get: function () {
			return inProgressViews;
		},
	});

	Object.defineProperty(self, 'currentMainView', {
		get: function () {
			return self.getCurrentMainView();
		},
	});

	return self;

	function removeFromHistory(object) {
		openedViews.forEach(function (oView) {
			removeFromParents(oView, object);
		});
		if (currentView) {
			removeFromParents(currentView, object);
		}
	}

	function removeFromParents(oView, object) {
		let view = oView;
		while (view.parentView) {
			if ((!object.EntityType || view.parentView.type === object.EntityType) && view.parentView.id === object.Id) {
				const parentParent = view.parentView.parentView;
				closeView(view.parentView, true);
				view.parentView = parentParent;
			} else {
				view = view.parentView;
			}
		}
	}

	function setCurrentView(view) {
		if (view && view.relatedView) {
			setCurrentView(view.relatedView);
			return;
		}
		const previousView = currentView;
		let branchChanged = true,
			stepForward = false,
			stepBack = false;
		if (view instanceof MxView) {
			stepForward = view.parentView === currentView;
			stepBack = currentView && currentView.parentView === view;
			branchChanged = !stepForward && /*!stepBack &&*/ currentView !== view;

			currentView = view;
			sideLayoutService.show();
			if (currentView.alwaysFullScreen || (stepBack && view.isWizard && sideLayoutService.fullScreenMode)) {
				sideLayoutService.switchFullScreenMode(true);
			} else if (stepBack && !currentView.parentView) {
				sideLayoutService.switchFullScreenMode(false);
			}
			setTimeout(function () {
				eventService.dispatchEvent(mx.shell.EventCodes.LayoutChanged, 'SidePanelCurrentViewChanged');
			}, 1);
		} else {
			closeRelatedViews(currentView);
			currentView = undefined;
			sideLayoutService.hide();
			sideLayoutService.switchFullScreenMode(false);
			leftSidebarService.showPanelByName('navigation');
		}

		eventService.dispatchEvent(mx.shell.EventCodes.SidePanelCurrentViewChanged, view);

		if (branchChanged && previousView) {
			openedViews.forEach(function (oView) {
				if (oView.parentView === currentView) {
					delete oView.parentView;
				}
			});
			closeBranch(previousView);
		}
	}

	function getCurrentView() {
		return currentView;
	}

	function closeBranch(view) {
		/* close all views (step by step from child to parent) which should be closed
			(close if the one is not opened or if the one is opened but is not visible in in-progress-panel, so user can't return to that view)
		) */
		while (view) {
			if (!view.keepAsOpened && view !== currentView) {
				closeView(view, true);
				view = view.parentView;
			} else {
				return;
			}
		}
	}

	function getCurrentMainView() {
		let mainCurrentView = currentView;

		while (mainCurrentView && mainCurrentView.related) {
			mainCurrentView = mainCurrentView.parentView;
		}

		return mainCurrentView;
	}

	function getOpenedViews() {
		return openedViews;
	}

	function getInProgressViews() {
		return openedViews.filter(function (oView) {
			return oView.keepAsOpened && !oView.related;
		});
	}

	function moveViewToInProgress(view) {
		if (!isViewOpened(view)) {
			return;
		}

		view.keepAsOpened = true;
		updateInProgressViewList();
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#addView
	 *
	 * @description
	 * Adds new {@ref mx.shell.DetailsView DetailsView} on this SidePanel.
	 * Also this {@ref mx.shell.DetailsView DetailsView} will got an `__actions` key with available corresponding actions.
	 * Finally it will dispatch an Event {@ref mx.shell.Event Event}
	 * with code: `mx.shell.EventCodes.SidePanelViewAdded`.
	 *
	 * @param {mx.shell.DetailsView} `view` - a view, that will be displayed on SidePanel.
	 * @param {mx.shell.DetailsView} `parentView` - a view, which will be added into adding 'view' as parentView. ParentView has to be already opened in side-panel or it will be ignored
	 * @param {Boolean} `related` - if true than this view is related to parentView and cannot exist without parentView.
	 * 								Such related view blocks its parentView, so if parentView is selected as currentView than currentView will be changed on related view automatically.
	 * 								If parentView is closed than relatedView also will be closed automatically
	 *
	 * @returns {Promise} finish interaction promise for added view
	 */
	function addView(view, parentView, related) {
		if (!view || sideLayoutService.keepClosed) {
			return $q.reject(mx.noLogCatchMessage('side-panel: no view or keepClosed'));
		}

		if (
			view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit ||
			view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.New ||
			view.isWizard
		) {
			view.keepAsOpened = true;
		}

		const openedView = openedViews.find(
			(v) =>
				v.type === view.type && ((view.id && v.id === view.id) || (!view.isWizard && v.context && view.context && view.context._id && v.context._id === view.context._id))
		);

		const sameView =
			openedView &&
			(view === openedView ||
				(view.dialogType === openedView.dialogType && contentWidgetService.getWidgetIdFromView(openedView) === contentWidgetService.getWidgetIdFromView(view)));
		const openedViewHasMorePriority =
			openedView &&
			view !== openedView &&
			isViewInProgress(openedView) &&
			(!view.keepAsOpened || openedView.dialogType !== mx.SolutionBuilderAgent.ContentWidgetDialogAction.Preview);
		const openedViewIsRelated = openedView && openedView.relatedView;

		if (sameView || openedViewHasMorePriority || openedViewIsRelated) {
			view = openedView;
		} else {
			if (openedView) {
				closeView(openedView, true);
			}

			view.opened = true;
			openedViews.push(view);

			if (parentView) {
				if (parentView instanceof WizardView) {
					parentView = getSidePanelView(parentView);
				}

				if (isViewOpened(parentView) || parentView === openedView) {
					view.parentView = parentView;
				}

				if (related && parentView && !parentView.relatedView) {
					//view can have only one related view which blocks its view
					view.related = true;
					parentView.relatedView = view;
				}
			}

			view.finishDefer = $q.defer();
			view.locals = view.locals || {};
			view.locals.finishDefer = view.finishDefer;
		}

		view.visible = true;

		setCurrentView(view);
		eventService.dispatchEvent(mx.shell.EventCodes.SidePanelViewAdded, view);
		updateInProgressViewList();

		view.finishDefer.promise
			.then(function () {
				if (view.isWizard) {
					_handleWizardClosed(view);
				}
			})
			.catch(mx.logCatch)
			.finally(function () {
				closeView(view);
			});

		return view.finishDefer.promise;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#getSidePanelView
	 *
	 * @description
	 * check if a view is one of opened view or it is a wizard page view of opened wizard and return opened side panel view
	 *
	 * @param {mx.shell.View} view to check if it is sidePanelView
	 * @returns {mx.shell.View | undefined} opened view or undefined if view is not opened in sidePanel
	 */
	function getSidePanelView(view) {
		return openedViews.find(function (openedView) {
			return openedView === view || (openedView.isWizard && openedView.getWizard() && openedView.getWizard().containsPageView(view));
		});
	}

	function externalCloseView(view) {
		if (view && view.finishDefer) {
			view.finishDefer.reject(mx.noLogCatchMessage('side-panel: externalCloseView'));
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#closeView
	 *
	 * @description
	 * close {@ref mx.shell.DetailsView DetailsView} on this SidePanel.
	 *
	 *
	 * @param {mx.shell.DetailsView | int | string} view - a view (link to object or its `id`),
	 *        that will be closed and removed from SidePanel.
	 * @param {boolean} soft - if true than view will be closed but won't be removed from chain
	 * 					(view.parentView will not be removed and currentView won't be automatically switched to parentView)
	 *
	 * @returns {mx.shell.DetailsView[]} updated list of {@ref mx.shell.DetailsView mx.shell.DetailsView}
	 */
	function closeView(view, soft) {
		let index;

		// If `view` is an `id` of removing object (not a link to object) lets find this view object
		if (view && typeof view !== 'object' && Array.isArray(openedViews)) {
			openedViews.forEach(function (oView) {
				if (oView.id === view || (angular.isString(oView.id) && angular.isString(view) && oView.id.toLowerCase() === view.toLowerCase())) {
					view = oView;
				}
			});
		}

		index = openedViews.indexOf(view);

		if (index > -1) {
			if (index > -1) {
				openedViews.splice(index, 1);
			}

			view.keepAsOpened = false;

			if (view.related && view.parentView) {
				delete view.parentView.relatedView;
				delete view.related;
			}

			// Fire Closing event
			eventService.dispatchEvent(mx.shell.EventCodes.SidePanelViewClosed, view);

			closeRelatedViews(view);

			openedViews.forEach(function (oView) {
				if (oView.parentView === view) {
					delete oView.parentView;
				}
			});

			if (!soft) {
				if (view === currentView) {
					if (!view.parentView || (view.parentView && isViewOpened(view.parentView))) {
						setCurrentView(view.parentView);
					} else {
						addView(view.parentView);
					}
				}

				if (view.parentView) {
					delete view.parentView;
				}
			}

			view.finishDefer.reject(mx.noLogCatchMessage('side-panel: closeView'));
			updateInProgressViewList();
		}

		return openedViews;
	}

	function closeCurrentView() {
		closeView(currentView);
	}

	function closeRelatedViews(view) {
		let relatedView = view && view.relatedView;

		while (relatedView) {
			delete relatedView.parentView;
			delete relatedView.related;
			relatedView = relatedView.relatedView;
		}

		if (view && view.relatedView) {
			closeView(view.relatedView);
			delete view.relatedView;
		}
	}

	function hideView(view) {
		if (view === currentView) {
			setCurrentView(undefined);
		}
	}

	function showView(view) {
		setCurrentView(view);
		if (!sideLayoutService.visible) {
			sideLayoutService.show();
		}
	}

	function onSearch(searchOptions) {
		if (searchOptions?.uiToken !== 'uiToken:search-page-content') {
			return;
		}
		if (currentView) {
			if (currentView.alwaysFullScreen) {
				setCurrentView(undefined);
			} else {
				sideLayoutService.switchFullScreenMode(false);
			}
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#switchToView
	 *
	 * @description
	 * Switch to passed {@ref mx.shell.DetailsView DetailsView} or its ID on this SidePanel if it exists.
	 *
	 * @param {mx.shell.DetailsView | string} view - `DetailsView` or its ID.
	 *
	 * @returns {mx.shell.DetailsView} current View
	 */
	function switchToView(view) {
		setCurrentView(
			openedViews.filter(function (v) {
				return view === v || view === v.id;
			})[0]
		);

		return currentView;
	}

	function getObjectDetailsFromView(view = {}) {
		const objectDetails = {
			Id: view.context?._id,
			EntityType: view.context?._type,
			DisplayString: view.context?._displayName,
			EntityDisplayName: view.headerTitle,
		};

		if (view.hideActions) {
			objectDetails.hideActions = view.hideActions;
		}

		if (view.isSingleView) {
			objectDetails.isSingleView = view.isSingleView;
		}

		if (view.readOnly) {
			objectDetails.readOnly = view.readOnly;
		}

		return objectDetails;
	}

	function handleItemSelectionOnMobile() {
		if (deviceService.screenMobile()) {
			$mdSidenav('mx-shell-nav').close();
		}
	}

	function updateInProgressViewList() {
		mx.components.Utils.replaceElements(inProgressViews, getInProgressViews());
		eventService.dispatchEvent(mx.shell.EventCodes.SidePanelInProgressViewsChanged, inProgressViews);
	}

	function isViewInProgress(rView) {
		return getInProgressViews().some(function (view) {
			return (rView.id === view.id && rView.type === view.type) || view === rView;
		});
	}

	function isViewOpened(rView) {
		return openedViews.some(function (view) {
			return (rView.id === view.id && rView.type === view.type) || view === rView;
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#isViewUnsafeForClosing
	 *
	 * @description
	 * Check if it is safe to close view
	 *
	 * @returns {boolean} is it unsafe to close view or no
	 */
	function isViewUnsafeForClosing(view) {
		return (
			view.dirty &&
			(view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.Edit ||
				view.dialogType === mx.SolutionBuilderAgent.ContentWidgetDialogAction.New ||
				view.isWizard)
		);
	}

	function refreshView(view) {
		eventService.dispatchEvent(mx.shell.EventCodes.RefreshCurrentView, { view: view });
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#showCloseTabConfirmationDialog
	 *
	 * @description
	 * Show confirmation dialog before closing view
	 *
	 * @param {mx.shell.DetailsView} view - a view that will be closed
	 *
	 * @returns {Promise} Promise that return updated list of {@ref mx.shell.DetailsView mx.shell.DetailsView} after confirmation dialog
	 */
	function showCloseTabConfirmationDialog(view) {
		const dialogConfig = {
			id: 'ActionOnSafeCloseTab',
			title: i18n.get('shell.mx-side-panel-service.unsavedData'),
			actions: [
				{
					id: 'cancel',
					notForSave: true,
					title: i18n.get('shell.mx-side-panel-service.doNothing'),
				},
				{
					id: 'close',
					title: i18n.get('shell.mx-side-panel-service.closeTab'),
				},
			],
		};

		if (!view.isWizard) {
			dialogConfig.actions.push({
				id: 'save-and-close',
				title: i18n.get('solutionBuilderAgent.services.side-panel-service.save-and-close'),
			});
		}

		return showConfirmationDialog(dialogConfig).then(function (answer) {
			if (answer === 'close') {
				closeView(view);
			} else if (answer === 'save-and-close') {
				eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.DialogActionExecuted, {
					view: view,
					action: 2,
				});
			}
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.SidePanelService#showConfirmationDialog
	 *
	 * @description
	 * Show confirmation dialog according to config (with possibility to save an answer)
	 *
	 * @param {object} dialogConfig - config of the confirmation dialog with array of actions
	 * @param {MxView} tabView - view from side-panel in which popup will be shown
	 *
	 * @returns {Promise} Promise that return answer from dialog
	 */

	function showConfirmationDialog(dialogConfig, tabView) {
		if (typeof dialogConfig !== 'object' || !dialogConfig.id) {
			return;
		}
		const def = $q.defer();
		let userId;

		return userProfileService
			.get()
			.then(function (userProfile) {
				userId = userProfile.id;
				return storage.get(dialogConfig.id + '::' + userId);
			})
			.then(function (savedAnswer) {
				if (savedAnswer) {
					return savedAnswer;
				} else {
					const buttons =
						'<md-button ng-repeat="button in vm.dialog.actions" ng-click="vm.action(button, vm.dialog.save)" ng-class="{\'md-confirm-button md-primary\': button.isConfirmButton}" ng-disabled="vm.dialog.save&&button.notForSave || button.disabled" mx-at-id="{{::button.id}}">{{::button.title}}</md-button>';

					const dialogTemplate =
						'' +
						'<md-dialog style="opacity:1" mx-at-id="{{::vm.dialog.id}}" role="dialog" aria-modal="true" aria-label=" " >' + // `aria-label` === " " here bacause $mdDialog compiles it such way that JAWS Screen Reader reads it twice
						'	<md-dialog-content class="md-dialog-content">' +
						'		<p class="mx-aria-only">' +
						i18n.get('shell.mx-side-panel-service.ARIA-confirmationDialogIntro') +
						' </p>' +
						'		<h1 class="md-title" style="margin-bottom:1rem;">{{::vm.dialog.title}}</h1><span class="mx-aria-only">.</span>' +
						'		<div class="md-dialog-content-body layout-column layout-align-center-start">' +
						'			<div class="mx-dialog-content-message">{{::vm.dialog.message}}</div>' +
						(dialogConfig.buttonsInActionZone ? '' : buttons) +
						'			<mx-checkbox ng-if="!vm.dialog.notSaved" ng-model="vm.dialog.save" data-label="' +
						i18n.get('shell.mx-side-panel-service.dontAskAgain') +
						'" aria-label="' +
						i18n.get('shell.mx-side-panel-service.ARIA-dontAskAgain') +
						'" style="margin-left:12px"></mx-checkbox>' +
						'		</div>' +
						'		<p class="mx-aria-only">' +
						i18n.get('shell.mx-side-panel-service.ARIA-confirmationDialogSummary') +
						' </p>' +
						'	</md-dialog-content>' +
						(dialogConfig.buttonsInActionZone ? '<md-dialog-actions>' + buttons + '</md-dialog-actions>' : '') +
						'</md-dialog>';

					const viewConfig = {
						id: dialogConfig.id,
						controllerAs: 'vm',
						controller: function controller() {
							this.dialog = dialogConfig;
							this.action = function action(button, needSave) {
								if (needSave && !button.notForSave) {
									storage.set(dialogConfig.id + '::' + userId, button.id);
								}
								if (tabView) {
									hidePopupInTab(tabView);
								} else {
									$mdDialog.hide();
								}
								def.resolve(button.id);
							};
						},
						template: dialogTemplate,
					};

					const popupView = new MxView(viewConfig);
					if (tabView) {
						showPopupInTab(tabView, popupView);
					} else {
						notificationService.customDialog(viewConfig).catch(function () {
							def.resolve('esc');
						});
					}
					return def.promise;
				}
			});
	}

	function showPopupInTab(tabView, popupView) {
		tabView.__showPopup = true;
		tabView.__popup = popupView;
	}

	function hidePopupInTab(view) {
		delete view.__popup;
		view.__showPopup = false;
	}

	function _handleWizardClosed(view) {
		const currentPage = view.locals.wizard.currentPage;
		if (currentPage && currentPage.dataModel && currentPage.dataModel.Context.__hideDetails) {
			if (Array.isArray(view.locals.selectedObjects)) {
				view.locals.selectedObjects.forEach(function (object) {
					const removingObj = {
						EntityType: object['Sys-Entity'] || object._type || object.type,
						Id: object.ID || object._id || object.id,
					};

					// force closing Side Panel
					removeFromHistory(removingObj);
				});
			}
		}
	}
}
